// Notion API service — Internal Integration Token + Offline-First
//
// All API responses are cached in IndexedDB so the app continues to work
// when offline. Cached data is served immediately; background refresh
// happens when online.
//
// API docs: https://developers.notion.com/reference/intro

import { getSetting, setSetting } from "./storage";
import type { NotionPage, NotionDatabase } from "./types";

// ---------------------------------------------------------------------------
// Cache keys
// ---------------------------------------------------------------------------

const CACHE_KEY_DATABASES = "notion-cache-databases";
const CACHE_KEY_ACTIVITY = "notion-cache-activity";
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CacheEntry<T> {
  data: T;
  cachedAt: number;
  workspaceId: string;
}

// ---------------------------------------------------------------------------
// NotionService
// ---------------------------------------------------------------------------

export class NotionService {
  private apiToken: string;
  private workspaceId: string;

  constructor(apiToken: string, workspaceId?: string) {
    this.apiToken = apiToken;
    this.workspaceId = workspaceId || 'default';
  }

  // ---------------------------------------------------------------------------
  // HTTP helper — attaches API token and handles offline gracefully
  // ---------------------------------------------------------------------------

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `https://api.notion.com/v1${path}`;
    console.log(`[Notion] ${options.method ?? "GET"} ${url}`);

    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${this.apiToken}`,
        'Notion-Version': '2022-06-28',
        Accept: "application/json",
        "Content-Type": "application/json",
        ...((options.headers as Record<string, string>) ?? {}),
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      console.error(`[Notion] ${response.status} ${url}: ${text.slice(0, 200)}`);
      throw new Error(`Notion API error ${response.status}: ${text || response.statusText}`);
    }

    return response.json() as Promise<T>;
  }

  // ---------------------------------------------------------------------------
  // Cache helpers
  // ---------------------------------------------------------------------------

  private async getCached<T>(key: string): Promise<T | null> {
    const entry = await getSetting<CacheEntry<T>>(key);
    if (!entry) return null;
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) return null;
    if (entry.workspaceId !== this.workspaceId) return null;
    return entry.data;
  }

  private async setCached<T>(key: string, data: T): Promise<void> {
    await setSetting<CacheEntry<T>>(key, { data, cachedAt: Date.now(), workspaceId: this.workspaceId });
  }

  /**
   * Offline-first fetch: return cached data immediately, refresh in background if online.
   * If no cache and offline: throws with a clear offline message.
   */
  private async fetchWithCache<T>(
    cacheKey: string,
    fetchFn: () => Promise<T>
  ): Promise<{ data: T; fromCache: boolean }> {
    const cached = await this.getCached<T>(cacheKey);
    if (cached) {
      // Background refresh if online
      if (navigator.onLine) {
        fetchFn()
          .then((fresh) => this.setCached(cacheKey, fresh))
          .catch((e) => console.warn("[Notion] Background refresh failed:", e));
      }
      return { data: cached, fromCache: true };
    }

    // No cache: must fetch
    const fresh = await fetchFn();
    await this.setCached(cacheKey, fresh);
    return { data: fresh, fromCache: false };
  }

  // ---------------------------------------------------------------------------
  // Public API methods
  // ---------------------------------------------------------------------------

  /**
   * List all accessible databases
   */
  async getDatabases(): Promise<{ databases: NotionDatabase[] }> {
    const cacheKey = CACHE_KEY_DATABASES;
    const { data } = await this.fetchWithCache(cacheKey, async () => {
      const results: NotionDatabase[] = [];
      let hasMore = true;
      let nextCursor: string | undefined;

      while (hasMore) {
        const response = await this.request<{ results: any[]; next_cursor?: string; has_more: boolean }>('/search', {
          method: 'POST',
          body: JSON.stringify({
            filter: {
              property: 'object',
              value: 'database'
            },
            page_size: 100,
            ...(nextCursor && { start_cursor: nextCursor })
          })
        });

        results.push(...response.results.map((r: any) => ({
          id: r.id,
          title: r.title?.[0]?.plain_text || 'Untitled',
          url: r.url,
          createdTime: r.created_time,
          lastEditedTime: r.last_edited_time,
          createdBy: r.created_by,
          lastEditedBy: r.last_edited_by,
          properties: r.properties,
          archived: r.archived || false
        })));

        hasMore = response.has_more;
        nextCursor = response.next_cursor;
      }

      return { databases: results };
    });

    return data;
  }

  /**
   * Get pages and activities within a date range
   */
  async getEvents(from: Date, to: Date): Promise<{ events: NotionPage[] }> {
    const cacheKey = `${CACHE_KEY_ACTIVITY}-${from.toISOString()}-${to.toISOString()}`;
    const { data } = await this.fetchWithCache(cacheKey, async () => {
      const results: NotionPage[] = [];
      let hasMore = true;
      let nextCursor: string | undefined;

      const fromDate = from.toISOString();
      const toDate = to.toISOString();

      while (hasMore) {
        const response = await this.request<{ results: any[]; next_cursor?: string; has_more: boolean }>('/search', {
          method: 'POST',
          body: JSON.stringify({
            filter: {
              and: [
                {
                  property: 'object',
                  value: 'page'
                },
                {
                  timestamp: 'last_edited_time',
                  last_edited_time: {
                    on_or_after: fromDate
                  }
                },
                {
                  timestamp: 'last_edited_time',
                  last_edited_time: {
                    on_or_before: toDate
                  }
                }
              ]
            },
            page_size: 100,
            sorts: [{
              timestamp: 'last_edited_time',
              direction: 'descending'
            }],
            ...(nextCursor && { start_cursor: nextCursor })
          })
        });

        results.push(...response.results.map((r: any) => ({
          id: r.id,
          title: r.properties?.title?.[0]?.plain_text || 'Untitled',
          url: r.url,
          createdTime: r.created_time,
          lastEditedTime: r.last_edited_time,
          createdBy: {
            id: r.created_by?.id,
            name: r.created_by?.name || 'Unknown',
            avatarUrl: r.created_by?.avatar_url
          },
          lastEditedBy: {
            id: r.last_edited_by?.id,
            name: r.last_edited_by?.name || 'Unknown',
            avatarUrl: r.last_edited_by?.avatar_url
          },
          parent: r.parent,
          parentType: r.parent?.type,
          archived: r.archived || false
        })));

        hasMore = response.has_more;
        nextCursor = response.next_cursor;
      }

      return { events: results };
    });

    return data;
  }

  /**
   * Get specific page details
   */
  async getPage(pageId: string): Promise<NotionPage> {
    const response = await this.request<any>(`/pages/${pageId}`);
    
    return {
      id: response.id,
      title: response.properties?.title?.[0]?.plain_text || 'Untitled',
      url: response.url,
      createdTime: response.created_time,
      lastEditedTime: response.last_edited_time,
      createdBy: {
        id: response.created_by?.id,
        name: response.created_by?.name || 'Unknown',
        avatarUrl: response.created_by?.avatar_url
      },
      lastEditedBy: {
        id: response.last_edited_by?.id,
        name: response.last_edited_by?.name || 'Unknown',
        avatarUrl: response.last_edited_by?.avatar_url
      },
      parent: response.parent,
      parentType: response.parent?.type,
      archived: response.archived || false
    };
  }
}

// ---------------------------------------------------------------------------
// Factory function
// ---------------------------------------------------------------------------

export async function createNotionService(): Promise<NotionService | null> {
  const apiToken = await getSetting<string>('notionApiKey');
  if (!apiToken) {
    console.warn("[Notion] No API token configured");
    return null;
  }

  try {
    // Test the token by fetching user info
    const response = await fetch('https://api.notion.com/v1/users/me', {
      headers: {
        Authorization: `Bearer ${apiToken}`,
        'Notion-Version': '2022-06-28',
      },
    });

    if (!response.ok) {
      console.error("[Notion] Invalid API token");
      return null;
    }

    const user = await response.json();
    console.log(`[Notion] Connected as: ${user.name || user.id}`);

    return new NotionService(apiToken, user.id);
  } catch (e) {
    console.error("[Notion] Failed to initialize service:", e);
    return null;
  }
}
